/**
 * AudioWaveform — bar waveform driven by a `peaks` array. Optional structure
 * overlay tints bars by section type (drop / build / main / breakdown / intro /
 * outro) so DJs can read the shape of an entire mix at a glance.
 *
 * Props:
 *   peaks       number[]   Required. e.g. from track.waveform_peaks
 *   height      number     px height (default 40)
 *   color       string     default (no structure) bar fill (slate)
 *   playedColor string     playhead / played bar fill (defaults to brand blue)
 *   progress    number     0..1 — fraction of the track already played
 *   onSeek      (fraction: number) => void
 *                          Optional. If provided, bars become clickable.
 *   structure   Array<{ type: string, start: number, end: number }>
 *                          Optional. Librosa `track.analysis.structure`
 *                          entries. When present, each bar is tinted by the
 *                          section that contains its timestamp.
 *   duration    number     Track length in seconds (required if `structure`
 *                          is passed — used to map bar-index → time).
 */

// Section palette: warm colours for high-energy sections, cool for steady/quiet.
const SECTION_COLORS = {
  drop:      '#F97316',  // warm orange — kick peaks
  build:     '#F59E0B',  // amber — rising energy
  main:      '#2563FF',  // brand cool blue — steady drive
  verse:     '#0EA5E9',  // sky
  breakdown: '#8B5CF6',  // violet — breakdown / breathe
  intro:     '#94A3B8',  // slate — muted
  outro:     '#94A3B8',  // slate — muted
};

function findSectionAt(structure, t) {
  if (!structure) return null;
  for (const s of structure) {
    if (t >= s.start && t < s.end) return s;
  }
  // Past the end of analysed sections → leave uncoloured (fall back to
  // default `color`) rather than smearing the last section across the
  // rest of the track. Analysis often caps at ~3 min but mixes can be
  // 2 h+, so we deliberately don't extrapolate.
  return null;
}

export default function AudioWaveform({
  peaks,
  height = 40,
  color = '#94A3B8',
  playedColor = '#2563FF',
  progress = 0,
  onSeek,
  structure = null,
  duration = 0,
  className = '',
}) {
  if (!Array.isArray(peaks) || peaks.length === 0) return null;

  const localMax = Math.max(...peaks, 0.01);
  const norm = peaks.map((p) => Math.min(1, p / localMax));

  const BAR_GAP_RATIO = 0.35;
  const step = 100 / norm.length;
  const barW = step * (1 - BAR_GAP_RATIO);
  const playedX = Math.max(0, Math.min(100, progress * 100));

  const hasStructure = Array.isArray(structure) && structure.length > 0 && duration > 0;

  const handleClick = (e) => {
    if (!onSeek) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, x / rect.width));
    onSeek(fraction);
  };

  return (
    <svg
      viewBox={`0 0 100 100`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className={className}
      onClick={onSeek ? handleClick : undefined}
      style={{ cursor: onSeek ? 'pointer' : 'default', display: 'block' }}
      aria-hidden={onSeek ? undefined : 'true'}
      role={onSeek ? 'slider' : undefined}
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      data-testid="audio-waveform"
    >
      {norm.map((v, i) => {
        const cx = i * step + step / 2;
        const isPlayed = cx <= playedX;

        // Pick fill: structure tint when available, else played/color default.
        let fill;
        if (hasStructure) {
          const barTime = (i / norm.length) * duration;
          const section = findSectionAt(structure, barTime);
          fill = (section && SECTION_COLORS[section.type]) || color;
        } else {
          fill = isPlayed ? playedColor : color;
        }

        const h = Math.max(1.5, v * 100);
        const y = (100 - h) / 2;
        // Played bars are brighter; unplayed dimmer + amplitude-modulated.
        const opacity = isPlayed
          ? 0.9 + v * 0.1
          : 0.45 + v * 0.35;
        return (
          <rect
            key={i}
            x={i * step + (step - barW) / 2}
            y={y}
            width={barW}
            height={h}
            rx={barW / 2}
            fill={fill}
            opacity={opacity}
          />
        );
      })}
      {progress > 0 && progress < 1 && (
        <line
          x1={playedX}
          x2={playedX}
          y1={0}
          y2={100}
          stroke={playedColor}
          strokeWidth={0.4}
          opacity={0.9}
        />
      )}
    </svg>
  );
}
